import DiceParser from '@3d-dice/dice-parser-interface';
import { parserCompatibleNotation } from './input_notation';

const AVAILABLE_DICE = new Set([4, 6, 8, 10, 12, 20, 100]);
const MAX_DICE = 100;

export const DICE_NOTATION_ERROR = 'Use Roll20 notation like 1d20+1d4, 2d6+4, 2d20kh, or 4d6dl1.';

export function parseDiceNotation(notation) {
  if (typeof notation !== 'string') throw new Error(DICE_NOTATION_ERROR);

  const normalized = notation.toLowerCase().replace(/\s+/g, '');
  if (!normalized) throw new Error(DICE_NOTATION_ERROR);

  const parser = new DiceParser();
  parser.rollParser.maxRollCount = MAX_DICE;

  let parsedGroups;
  try {
    parsedGroups = parser.parseNotation(parserCompatibleNotation(normalized));
  } catch {
    throw new Error(DICE_NOTATION_ERROR);
  }

  const groups = parsedGroups.map(({ qty, sides, mods }) => ({
    qty: Number(qty),
    sides: Number(sides),
    mods
  }));
  const diceCount = groups.reduce((total, group) => total + group.qty, 0);
  const validGroups = groups.every(
    ({ qty, sides }) => Number.isSafeInteger(qty) && qty > 0 && AVAILABLE_DICE.has(sides)
  );

  if (!groups.length || !validGroups || diceCount > MAX_DICE) {
    throw new Error(DICE_NOTATION_ERROR);
  }

  return { normalized, parser, groups, diceCount };
}

export async function rollDiceNotation(diceBox, notation) {
  const parsedNotation = parseDiceNotation(notation);
  let diceCount = parsedNotation.diceCount;

  await diceBox.roll(parsedNotation.groups);
  let results = diceBox.getRollResults();
  let rerolls = parsedNotation.parser.handleRerolls(results);

  while (rerolls.length) {
    diceCount += rerolls.reduce((total, group) => total + group.qty, 0);
    if (diceCount > MAX_DICE) throw new Error('This roll requires too many rerolls.');

    await diceBox.add(rerolls, { newStartPoint: false });
    results = diceBox.getRollResults();
    rerolls = parsedNotation.parser.handleRerolls(results);
  }

  const result = Number(parsedNotation.parser.parseFinalResults(results).value);
  if (!Number.isSafeInteger(result)) {
    throw new Error('Dice Box returned a result that cannot be saved.');
  }

  return result;
}
